import {OperationElementAnnexe} from "../../common/types/Types";
import React, {useCallback, useContext, useEffect, useState} from "react";
import Loader from "../../common/views/Loader";
import BYSwitch from "../../common/inputs/BYSwitch";
import {useAlert} from 'react-alert';
import {AuthContext} from "../../providers/AuthProvider";
import {useOperation, useUpdateOperationElements} from "../../api/hooks";
import BYAsyncButton from "../../common/buttons/BYAsyncButton";
import BYExportButtonsTable from "../../common/buttons/BYExportButtonsTable";

interface KeyDocumentListProps {
    data: OperationElementAnnexe[];
    operationId?: number;
    lotId?: number;
    loading?: boolean;
}

interface HandleChangeParams {
    colSansObjet?: boolean;
    colNonRemis?: string;
    colSaisie?: string;
    colRemis?: string;
}

interface GetNewOperationElementAnnexeParams extends HandleChangeParams {
    elementAnnexeId: number;
}

const KeyDocumentList = (props: KeyDocumentListProps) => {
    const {data, operationId, loading} = props;
    const {additionalElements} = useContext(AuthContext);
    const [values, setValues] = useState([]);
    const alert = useAlert();
    const {mutateAsync: mutateOperationElements} = useUpdateOperationElements();
    const {data: operation} = useOperation(operationId);

    useEffect(() => {
        setValues(data);
    }, [data]);

    const getNewOperationElementAnnexe = useCallback(
        ({
             elementAnnexeId,
             colSansObjet = false,
             colNonRemis = '',
             colSaisie = '',
             colRemis = '',
         }: GetNewOperationElementAnnexeParams) => ({
            operationRefOperation: operationId,
            elementAnnexeRefElementAnnexe: elementAnnexeId,
            colSansObjet,
            colNonRemis,
            colSaisie,
            colRemis,
        }), [operationId]);

    const handleChange = useCallback((params: HandleChangeParams, elementAnnexe, value) => {
        const {
            colSansObjet,
            colNonRemis,
            colSaisie,
            colRemis
        } = params;

        const newValues = [...values];
        const index = values.findIndex(v => v === value);

        if (value) {
            newValues[index] = {
                ...value,
                ...(colSansObjet !== undefined ? {colSansObjet} : {}),
                ...(colNonRemis !== undefined ? {colNonRemis} : {}),
                ...(colSaisie !== undefined ? {colSaisie} : {}),
                ...(colRemis !== undefined ? {colRemis} : {})
            };
        } else {
            newValues.push(getNewOperationElementAnnexe({
                elementAnnexeId: elementAnnexe.refElementAnnexe,
                colSansObjet,
                colNonRemis,
                colSaisie,
                colRemis
            }));
        }

        setValues(newValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values, setValues]);

    if (loading) {
        return <div className="text-center my-6 h-100"><Loader/></div>;
    }

    const keys = additionalElements.filter(elAn => elAn.typeElementRefTypeElement === 1);
    const documents = additionalElements.filter(elAn => elAn.typeElementRefTypeElement === 5);

    return <div className="row">
        <div className="col mr-1">

            <h5 className="text-success">
                Clefs remises&nbsp;&nbsp;&nbsp;
                <BYExportButtonsTable
                    data={[['', 'Numéro', 'Remis', 'Nb remis', 'Nb non remis']].concat(keys.map((elementAnnexe, index) => {
                        const value = values.find(opElAn => opElAn.elementAnnexeRefElementAnnexe === elementAnnexe.refElementAnnexe);

                        return [
                            elementAnnexe.libElement,
                            value?.colSaisie,
                            value?.colSansObjet ? 'Oui' : 'Non',
                            value?.colRemis,
                            value?.colNonRemis
                        ];
                    }))} title={`Clefs remises ${operation?.nomOperation}`}/>
            </h5>

            <table className="table table-by">
                <thead className="table-dark">
                <tr>
                    <th></th>
                    <th>Numéro</th>
                    <th>Remis</th>
                    <th>Nb remis</th>
                    <th>Nb non remis</th>
                </tr>
                </thead>
                <tbody>
                {keys.map((elementAnnexe, index) => {
                    const value = values.find(opElAn => opElAn.elementAnnexeRefElementAnnexe === elementAnnexe.refElementAnnexe);

                    return <tr key={`keys-tr-${index}`}>
                        <td className="col">
                            <label className="line-height-2x">
                                {elementAnnexe.libElement}
                            </label>
                        </td>
                        <td>
                            <input type="text" className="form-control form-control-sm" value={value?.colSaisie}
                                   style={{minWidth: '200px'}}
                                   onChange={(e) => {
                                       const {value: colSaisie} = e.target;

                                       handleChange({colSaisie}, elementAnnexe, value);
                                   }}
                            />
                        </td>
                        <td>
                            <BYSwitch
                                checked={value?.colSansObjet}
                                onChange={(checked) => {
                                    handleChange({colSansObjet: checked}, elementAnnexe, value);
                                }}
                            />
                        </td>
                        <td>
                            <input type="number" className="form-control form-control-sm" value={value?.colRemis}
                                   onChange={(e) => {
                                       const {value: colRemis} = e.target;

                                       handleChange({colRemis}, elementAnnexe, value);
                                   }}/>
                        </td>
                        <td>
                            <input type="number" className="form-control form-control-sm" value={value?.colNonRemis}
                                   onChange={(e) => {
                                       const {value: colNonRemis} = e.target;

                                       handleChange({colNonRemis}, elementAnnexe, value);
                                   }}/>
                        </td>
                    </tr>;
                })}
                </tbody>
            </table>
        </div>
        <div className="col ml-1">
            <div className="row">
                <div className="col">
                    <h5 className="text-success">
                        Documents remis&nbsp;&nbsp;&nbsp;
                        <BYExportButtonsTable
                            data={[['', 'Sans objet', 'Remis', 'Non remis']].concat(documents.map((elementAnnexe) => {
                                    const value = values.find(opElAn => opElAn.elementAnnexeRefElementAnnexe === elementAnnexe.refElementAnnexe);

                                return [
                                    elementAnnexe.libElement,
                                    value?.colSansObjet ? 'Oui' : '',
                                    value?.colRemis === '1'  ? 'Oui' : '',
                                    value?.colNonRemis === '1' ? 'Oui' : '',
                                ];
                            }))} title={`Documents remis ${operation?.nomOperation}`}/>
                    </h5>
                </div>
                <div className="col-5 text-right d-flex justify-content-end align-items-baseline">
                    <BYAsyncButton
                        className="btn btn-secondary btn-sm mr-2"
                        onClick={async () => {
                            try {
                                await mutateOperationElements({
                                    opAnnexElements: values,
                                    operationId,
                                    withUpdate: false
                                });

                                alert.success('Clés et documents mises à jour');
                            } catch (e) {
                                alert.error('Erreur lors de la mise à jour de clés et documents');
                            }
                        }}
                    >
                        Sauvegarder{' '}
                        <i className="fa fa-save"/>
                    </BYAsyncButton>

                    <BYAsyncButton
                        className="btn btn-warning btn-sm"
                        onClick={async () => {
                            try {
                                await mutateOperationElements({
                                    opAnnexElements: values,
                                    operationId,
                                    withUpdate: true
                                });

                                alert.success('Clés et documents mises à jour');
                            } catch (e) {
                                alert.error('Erreur lors de la mise à jour de clés et documents');
                            }
                        }}
                    >
                        Propager aux Lots{' '}
                        <i className="fa fa-network-wired"/>
                    </BYAsyncButton>
                </div>
            </div>

            <table className="table table-by">
                <thead className="table-dark">
                <tr>
                    <th></th>
                    <th>Sans objet</th>
                    <th>Remis</th>
                    <th>Non remis</th>
                </tr>
                </thead>
                <tbody>
                {documents.map((elementAnnexe, index) => {
                    const key = `documents-tr-${index}`;
                    const value = values.find(opElAn => opElAn.elementAnnexeRefElementAnnexe === elementAnnexe.refElementAnnexe);

                    let checked;
                    if (value?.colRemis === '1') {
                        checked = 1;
                    } else if (value?.colNonRemis === '1') {
                        checked = 2;
                    } else {
                        checked = 0;
                    }

                    const onChange = (e) => {
                        const {value: radioButtonValue} = e.target;

                        const newValues: HandleChangeParams = {};

                        if (radioButtonValue === '0') {
                            newValues.colSansObjet = true;
                            newValues.colNonRemis = '0';
                            newValues.colRemis = '0';
                        } else if (radioButtonValue === '1') {
                            newValues.colSansObjet = false;
                            newValues.colNonRemis = '0';
                            newValues.colRemis = '1';
                        } else {
                            newValues.colSansObjet = false;
                            newValues.colNonRemis = '1';
                            newValues.colRemis = '0';
                        }

                        handleChange(newValues, elementAnnexe, value);
                    };

                    return <tr key={key}>
                        <td>
                            <label>{elementAnnexe.libElement}</label>
                        </td>
                        <td className="text-center">
                            <input className="form-check-input"
                                   type="radio"
                                   name={key}
                                   id={key}
                                   value={0}
                                   checked={checked === 0}
                                   onChange={onChange}
                            />
                        </td>
                        <td className="text-center">
                            <input className="form-check-input"
                                   type="radio"
                                   name={key}
                                   id={key}
                                   value={1}
                                   checked={checked === 1}
                                   onChange={onChange}
                            />
                        </td>
                        <td className="text-center">
                            <input className="form-check-input"
                                   type="radio"
                                   name={key}
                                   id={key}
                                   value={2}
                                   checked={checked === 2}
                                   onChange={onChange}
                            />
                        </td>
                    </tr>;
                })}
                </tbody>
            </table>
        </div>
    </div>;
};

export default KeyDocumentList;
